// src/components/team/DisengagedParentCard.jsx
//
// Smart-action card on the Team Detail page that surfaces when 1+
// parents on the team have been inactive for 14+ days. Tapping the
// CTA routes to the Re-engagement Review screen.
//
// Approved spec: features/disengaged-parent/{01-requirements.md,
// 02-wireframe.md, 03-architecture.md}.
//
// Data: fetches once on mount via the `getDisengagedParents` callable.
// AD8 specified no real-time listener — disengagement is a slow-
// moving signal and a one-shot read on Team Detail load is enough.
//
// States:
//   - loading: skeleton card (matches existing skeleton pattern)
//   - error:   renders null + console.warn (per wireframe §3)
//   - empty:   renders null when count === 0 (no empty placeholder)
//   - success: card visible with count + name preview + CTA
//
// Bilingual: coach-facing, English-only acceptable per wireframe §3.
// All translatable strings live under `smartActions.disengagedParent.*`.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import clsx from 'clsx';
import { UsersIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * Build the name-preview string per D7:
 *   - 1 name:  "Maria"
 *   - 2 names: "Maria and John"
 *   - 3+:      "Maria, John, and N other(s)"
 * Returns null when there are no names (caller renders nothing).
 */
function buildNamePreview(t, names) {
  const clean = (names || []).map((n) => String(n || '').trim()).filter(Boolean);
  if (clean.length === 0) return null;
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) {
    return t('smartActions.disengagedParent.namesPreviewTwo', {
      name1: clean[0],
      name2: clean[1],
    });
  }
  // 3+ — show first two then "and N other(s)".
  return t('smartActions.disengagedParent.namesPreviewMore', {
    name1: clean[0],
    name2: clean[1],
    count: clean.length - 2,
  });
}

export default function DisengagedParentCard({ teamId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', count: 0, names: [] });

  useEffect(() => {
    if (!teamId) {
      setState({ status: 'empty', count: 0, names: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const fns = getFunctions(undefined, 'us-central1');
        const callable = httpsCallable(fns, 'getDisengagedParents');
        const res = await callable({ teamId });
        if (cancelled) return;
        const data = res?.data || {};
        const list = Array.isArray(data.disengaged) ? data.disengaged : [];
        setState({
          status: list.length > 0 ? 'ready' : 'empty',
          count: Number(data.count) || list.length,
          // Per D7: display name preview uses the first 2 displayNames
          // (the FULL display name, NOT firstName — the SMS body uses
          // firstName only per F3, but the card preview shows what
          // the coach actually recognizes).
          names: list.slice(0, 2).map((p) => p.displayName || p.firstName || ''),
        });
      } catch (err) {
        // Wireframe §3 error state: do not render, log silently.
        if (!cancelled) {
          console.warn('[DisengagedParentCard] fetch failed', err?.code || err?.message);
          setState({ status: 'error', count: 0, names: [] });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // Empty + error: render nothing.
  if (state.status === 'empty' || state.status === 'error') return null;

  // Loading: render NOTHING (2026-07-24, reversing the wireframe §3
  // skeleton). The common case — every new team, and most teams most
  // days — resolves to empty, so a skeleton promises content that
  // usually never arrives: a mystery gray box on a fresh coach's first
  // team-page load (several seconds on a function cold start). Teams
  // that DO have disengaged parents get the card popping in at the top
  // instead; that layout shift is the cheaper cost.
  if (state.status === 'loading') return null;

  // Success: the actual card.
  const count = state.count;
  const titleKey =
    count === 1
      ? 'smartActions.disengagedParent.cardCountSingular'
      : 'smartActions.disengagedParent.cardCountPlural';
  const namesPreview = buildNamePreview(t, state.names);

  return (
    <div className="mb-5">
      <p
        className="mb-2.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
        style={{ fontFamily: HEAD_FONT }}
      >
        <UsersIcon className="h-3.5 w-3.5" />
        {t('smartActions.disengagedParent.cardTitle')}
      </p>
      <button
        type="button"
        onClick={() => navigate(`/teams/${teamId}/reengagement`)}
        className={clsx(
          'w-full text-left flex items-center gap-3 rounded-xl border px-3.5 py-3 min-w-0',
          'border-[#b5f03d]/40 bg-[#b5f03d]/10 hover:bg-[#b5f03d]/20 transition-colors'
        )}
        aria-label={t('smartActions.disengagedParent.ctaSend')}
      >
        <div className="flex-shrink-0 rounded-lg p-2 bg-[#b5f03d]/30">
          <UsersIcon className="h-4 w-4 text-[#5a8a1f]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0d1f14] truncate">
            {t(titleKey, { count })}
          </p>
          {namesPreview && (
            <p className="text-xs text-zinc-600 truncate">{namesPreview}</p>
          )}
        </div>
        <ArrowRightIcon className="flex-shrink-0 h-4 w-4 text-[#5a8a1f]" />
      </button>
    </div>
  );
}
